import { useCallback } from "react";
import { useMap } from "./useMap";
import { useVectorSourceContext } from "./useVectorSourceContext";
import Feature from "ol/Feature";
import { Geometry } from "ol/geom";
import VectorSource from "ol/source/Vector";

/**
 * 맵의 모든 VectorSource에서 ID로 Feature를 찾는 훅.
 * ModifyAnnotation, DeleteAnnotation, MoveAnnotation에서 중복 구현된 로직을 통합합니다.
 */
export function useFeatureFinder() {
  const map = useMap();
  const { getVectorSources } = useVectorSourceContext();

  // 맵의 모든 레이어에서 VectorSource 수집
  const collectAllVectorSources = useCallback((): VectorSource[] => {
    const found: VectorSource[] = [];

    const walk = (group: any) => {
      if (!group) return;
      const layers = group.getLayers?.().getArray?.() || [];
      layers.forEach((ly: any) => {
        const src = ly.getSource?.();
        if (src && typeof src.getFeatureById === "function") {
          found.push(src);
        }
        if (typeof ly.getLayers === "function") {
          walk(ly);
        }
      });
    };

    map.getLayers().getArray().forEach((ly: any) => {
      const src = ly.getSource?.();
      if (src && typeof src.getFeatureById === "function") {
        found.push(src);
      }
      if (typeof ly.getLayers === "function") {
        walk(ly);
      }
    });

    return found;
  }, [map]);

  // 등록된 소스 + 맵 전체 레이어를 합쳐 ID로 Feature 탐색
  const findFeatureById = useCallback(
    (featureId: string): Feature<Geometry> | null => {
      const registeredSources = getVectorSources?.() || [];
      const collectedSources = collectAllVectorSources();

      // 중복 제거: 등록된 소스 우선
      const seen = new Set<VectorSource>(registeredSources);
      const allSources = [...registeredSources];
      for (const src of collectedSources) {
        if (!seen.has(src)) {
          seen.add(src);
          allSources.push(src);
        }
      }

      for (const src of allSources) {
        const feature = src.getFeatureById(featureId);
        if (feature && typeof feature.getGeometry === "function") {
          return feature as Feature<Geometry>;
        }
      }

      return null;
    },
    [getVectorSources, collectAllVectorSources]
  );

  return { findFeatureById, collectAllVectorSources };
}
